import { useParams } from 'wouter';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Skeleton,
  Chip,
  Tooltip,
} from '@nextui-org/react';
import LiquidationDetailsTable from '../../../../components/tables/settlement-details-table';
import { Icon } from '@iconify/react';
import LiquidationFOBTable from '../../../../components/tables/liquidation[id]-fob-table';
import { type ReactNode, useEffect, useState } from 'react';
import {
  Incoterms,
  Settlement,
  SettlementPerformance,
} from 'common/src/utils/types';
import { getSettlementById } from 'common/src/api/settlements';
import {
  calculateWeightedAverage,
  getSettlementIncoterm,
} from 'common/src/utils/functions';
import SettlementsLights from '../../../../components/pallets/settlements-lights';
import {
  formatDate,
  formatMoneyUSD,
  formatMoneyRMB,
  formatNumber,
} from '../../../../utils/functions';
import EditSettlementDialog from '../../../../components/settlements/edit-settlement-dialog';
import MissingInfoCard from '../../../../components/settlements/missing-info-card';
import { logUserAction } from '../../../../services/actionsService';
import { useTranslation } from '../../../../../../common/src/hooks/useTranslation';
import useDataStore from '../../../../../../common/src/stores/useDataStore';
import CostStandardToggle from '../../../../components/settlements/cost-standard-toggle';
import {
  CostStandard,
  useExporterCostView,
} from '../../../../hooks/useExporterCostView';
import { BackButton, PageHeader, PageLayout } from '../../../../components/ui';

interface AnalysisMetricProps {
  label: string;
  value: ReactNode;
  icon: string;
  iconClassName: string;
  valueClassName?: string;
  valueCaption?: string;
  metrics?: Array<{
    label: string;
    value: ReactNode;
  }>;
}

const AnalysisMetric = ({
  label,
  value,
  icon,
  iconClassName,
  valueClassName = 'text-foreground',
  valueCaption,
  metrics,
}: AnalysisMetricProps) => (
  <div className='h-full rounded-medium border border-default-200 p-4'>
    <div className='flex items-start justify-between gap-3'>
      <div className='min-w-0'>
        <p className='text-xs uppercase tracking-wide text-default-500'>
          {label}
        </p>
        <p
          className={`mt-2 text-xl font-semibold leading-tight break-words ${valueClassName}`}
        >
          {value}
        </p>
        {valueCaption && (
          <p className='mt-1 text-[11px] font-semibold uppercase text-default-400'>
            {valueCaption}
          </p>
        )}
      </div>
      <div className={`shrink-0 rounded-md p-2 ${iconClassName}`}>
        <Icon icon={icon} width={20} height={20} />
      </div>
    </div>
    {!!metrics?.length && (
      <div className='mt-4 grid grid-cols-2 gap-2'>
        {metrics.map((metric) => (
          <div
            key={metric.label}
            className='rounded-md bg-default-50 px-3 py-2'
          >
            <p className='text-[11px] font-semibold uppercase text-default-400'>
              {metric.label}
            </p>
            <p className='mt-1 text-sm font-semibold text-default-700 break-words'>
              {metric.value}
            </p>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Indicador de "Análisis Venta": mismo formato que KpiCard (etiqueta arriba,
// valor abajo) en un bloque con borde. Ver design.md › Patrones repetidos.
const SaleIndicator = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className='flex h-full flex-col items-start gap-2 rounded-medium border border-default-200 p-4'>
    <p className='text-xs uppercase tracking-wide text-default-500'>{label}</p>
    {children}
  </div>
);

interface AnalysisSectionProps {
  title: string;
  children?: ReactNode;
  summary?: ReactNode;
}

const AnalysisSection = ({
  title,
  children,
  summary,
}: AnalysisSectionProps) => (
  <section className='rounded-medium border border-default-200 p-4'>
    <div
      className={`flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between ${
        children ? 'mb-3' : ''
      }`}
    >
      <h3 className='text-sm font-semibold text-default-700'>{title}</h3>
      {summary}
    </div>
    {children}
  </section>
);

interface CostSummaryProps {
  totalUsd: number;
  totalKgs: number;
  boxSize: number;
  totalLabel: string;
  perKgLabel: string;
  equivalentBoxLabel: string;
  emphasized?: boolean;
}

const CostSummary = ({
  totalUsd,
  totalKgs,
  boxSize,
  totalLabel,
  perKgLabel,
  equivalentBoxLabel,
  emphasized = false,
}: CostSummaryProps) => {
  const perKg = totalKgs > 0 ? totalUsd / totalKgs : 0;
  const metrics = [
    { label: totalLabel, value: formatMoneyUSD(totalUsd) },
    {
      label: perKgLabel,
      value: formatMoneyUSD(perKg, { decimals: 2 }),
    },
    {
      label: equivalentBoxLabel,
      value: formatMoneyUSD(perKg * boxSize, { decimals: 2 }),
    },
  ];

  return (
    <div className='flex flex-wrap items-baseline gap-x-4 gap-y-1 sm:justify-end'>
      {metrics.map((metric) => (
        <div key={metric.label} className='flex items-baseline gap-1.5'>
          <span className='text-[10px] font-semibold uppercase text-default-400'>
            {metric.label}
          </span>
          <span
            className={`text-sm text-default-700 ${
              emphasized ? 'font-bold' : 'font-semibold'
            }`}
          >
            {metric.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const SettlementId = () => {
  const { t } = useTranslation('settlements');
  const { id } = useParams();
  const { selectedSpecies } = useDataStore();
  const boxSize = selectedSpecies?.default_unit_price || 5;
  const [settlement, setSettlement] = useState<Settlement | null>(null);
  const [totalKgs, setTotalKgs] = useState<number>(0);
  const [performance, setPerformance] = useState<SettlementPerformance | null>(
    null
  );
  const [averagePercentage, setAveragePercentage] = useState<number>(0);
  const [averageSaleEqBox, setAverageSaleEqBox] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [totalFob, setTotalFob] = useState<number>(0);
  const [incoterm, setIncoterm] = useState<Incoterms | null>();
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  // Additional summary metrics
  const [avgDiffETA, setAvgDiffETA] = useState<number>(0);
  const [avgDiffTRX, setAvgDiffTRX] = useState<number>(0);
  const [waitDays, setWaitDays] = useState<number | null>(null);
  const [waitValueDiff, setWaitValueDiff] = useState<number>(0);
  const [waitValueMoney, setWaitValueMoney] = useState<number>(0);
  const [waitMoneyTrx, setWaitMoneyTrx] = useState<number>(0);
  const [waitMoneyEta, setWaitMoneyEta] = useState<number>(0);
  const [performanceEta, setPerformanceEta] =
    useState<SettlementPerformance | null>(null);
  const [costStandard, setCostStandard] = useState<CostStandard>('fm');
  const exporterCostView = useExporterCostView(
    settlement?.exporter_id,
    settlement ? [settlement.id] : []
  );

  useEffect(() => {
    if (settlement) {
      logUserAction('view', 'page', 'settlements-details-id', {
        id,
        reference_number: settlement.reference_number,
        incoterm: getSettlementIncoterm(settlement),
      });
    }
  }, [settlement]);

  useEffect(() => {
    fetchDetails();
  }, [id]);

  if (!id) {
    return <p>{t('No se encontró ese id')}</p>;
  }

  const fetchDetails = async () => {
    const settlement = await getSettlementById(id);

    setSettlement(settlement as Settlement);
    setIncoterm(getSettlementIncoterm(settlement || ({} as Settlement)));
    // Base settlement loaded; metrics recalculated in effect below
    // console.log('details', details);
    let totalKilos = 0;
    settlement?.settlement_details?.forEach((detail) => {
      totalKilos += detail.kilos;
    });
    // Average percentage recalculated in effect below
    setAverageSaleEqBox(
      ((settlement?.total_revenue_local || 0) / totalKilos) * boxSize
    );
    setTotalKgs(totalKilos);
    setTotalFob(
      (settlement?.total_revenue_local -
        settlement?.total_tax_local -
        settlement?.total_other_costs_local -
        (settlement?.total_repacking_costs_local || 0) -
        settlement?.total_commission_local) /
        settlement?.exchange_rate -
        settlement?.total_freight_usd
    );
    setLoading(false);
  };

  // Recalculate performance and average percentage when data updates
  useEffect(() => {
    if (!settlement) return;
    // Compute performance using ETA comparison (quartiles)
    const quartileGroups = settlement.settlement_details?.reduce(
      (acc, detail) => {
        const quart = detail.quartil_vs_market_eta;
        const kilos = detail.kilos || 0;
        if (quart === null || quart === undefined) {
          acc['null'] = (acc['null'] || 0) + kilos;
        } else {
          acc[quart] = (acc[quart] || 0) + kilos;
        }
        return acc;
      },
      {} as Record<string | number, number>
    );
    const total =
      Object.values(quartileGroups || {}).reduce((s, v) => s + v, 0) || 0;
    const perf = {
      q1: Math.round(
        (((quartileGroups?.[1] || 0) as number) / (total || 1)) * 100
      ),
      q2: Math.round(
        (((quartileGroups?.[2] || 0) as number) / (total || 1)) * 100
      ),
      q3: Math.round(
        (((quartileGroups?.[3] || 0) as number) / (total || 1)) * 100
      ),
      q4: Math.round(
        (((quartileGroups?.[4] || 0) as number) / (total || 1)) * 100
      ),
      nulls: Math.round(
        (((quartileGroups?.['null'] || 0) as number) / (total || 1)) * 100
      ),
    } as SettlementPerformance;
    setPerformance(perf);

    // Compute weighted average difference using ETA comparison
    const avgDifference = calculateWeightedAverage(
      (settlement?.settlement_details || []).map((detail) => ({
        kilos: detail.kilos || 0,
        value: detail.difference_vs_market_eta,
      })),
      (item) => item.value
    );
    setAveragePercentage(avgDifference);
  }, [settlement]);

  // Compute ETA/TRX metrics and waiting deltas when settlement changes
  useEffect(() => {
    if (!settlement) return;
    const details = settlement.settlement_details || [];
    const trx = calculateWeightedAverage(
      details.map((d) => ({
        kilos: d.kilos || 0,
        value: d.difference_vs_market,
      })),
      (i) => i.value
    );
    const eta = calculateWeightedAverage(
      details.map((d) => ({
        kilos: d.kilos || 0,
        value: d.difference_vs_market_eta,
      })),
      (i) => i.value
    );
    setAvgDiffTRX(trx);
    setAvgDiffETA(eta);
    // Diferencia valor espera: Fecha de venta (TRX) - ETA
    setWaitValueDiff((trx || 0) - (eta || 0));

    // Days of wait = date_sold - ETA (in days)
    const etaDate = settlement.ship_trip?.eta
      ? new Date(settlement.ship_trip.eta)
      : null;
    const soldDate = settlement.date_sold
      ? new Date(settlement.date_sold)
      : null;
    if (etaDate && soldDate) {
      const diffMs = soldDate.getTime() - etaDate.getTime();
      setWaitDays(Math.round(diffMs / (1000 * 60 * 60 * 24)));
    } else {
      setWaitDays(null);
    }

    // Performance ETA
    const groups = details.reduce(
      (acc, d) => {
        const q = d.quartil_vs_market_eta as number | null;
        const k = d.kilos || 0;
        if (q === null || q === undefined) acc['null'] = (acc['null'] || 0) + k;
        else acc[q] = (acc[q] || 0) + k;
        return acc;
      },
      {} as Record<string | number, number>
    );
    const total =
      Object.values(groups).reduce((s, v) => s + (v as number), 0) || 0;
    setPerformanceEta({
      q1: Math.round((((groups[1] || 0) as number) / (total || 1)) * 100),
      q2: Math.round((((groups[2] || 0) as number) / (total || 1)) * 100),
      q3: Math.round((((groups[3] || 0) as number) / (total || 1)) * 100),
      q4: Math.round((((groups[4] || 0) as number) / (total || 1)) * 100),
      nulls: Math.round(
        (((groups['null'] || 0) as number) / (total || 1)) * 100
      ),
    });

    // Diferencia valor espera en dinero (sumatoria por detalle),
    // replicando el cálculo de la tabla por línea: diff = total - total/(1+pct)
    let sumMoneyTrx = 0;
    let sumMoneyEta = 0;
    details.forEach((d) => {
      const totalLocal = Number(d.local_total_price);
      if (!isFinite(totalLocal)) return;
      const pctTrx =
        typeof d.difference_vs_market === 'number' &&
        isFinite(d.difference_vs_market)
          ? d.difference_vs_market
          : null;
      const pctEta =
        typeof d.difference_vs_market_eta === 'number' &&
        isFinite(d.difference_vs_market_eta)
          ? d.difference_vs_market_eta
          : null;
      const moneyDiff = (pct: number | null) => {
        if (pct == null) return 0;
        const f = 1 + pct / 100;
        if (!isFinite(f) || f === 0) return 0;
        const diff = totalLocal - totalLocal / f;
        return isFinite(diff) ? diff : 0;
      };
      sumMoneyTrx += moneyDiff(pctTrx);
      sumMoneyEta += moneyDiff(pctEta);
    });
    setWaitMoneyTrx(sumMoneyTrx);
    setWaitMoneyEta(sumMoneyEta);
    setWaitValueMoney(sumMoneyTrx - sumMoneyEta);
  }, [settlement]);

  const exchangeRate = settlement?.exchange_rate || 1;
  const totalRevenueLocal = settlement?.total_revenue_local || 1;
  const commissionPercent =
    ((settlement?.total_commission_local || 0) / totalRevenueLocal) * 100;
  const taxPercent =
    ((settlement?.total_tax_local || 0) / totalRevenueLocal) * 100;
  const commissionUsd =
    (settlement?.total_commission_local || 0) / exchangeRate;
  const taxUsd = (settlement?.total_tax_local || 0) / exchangeRate;
  const totalCommercialCostsUsd = commissionUsd + taxUsd;
  const freightUsd = settlement?.total_freight_usd || 0;
  const repackingUsd =
    (settlement?.total_repacking_costs_local || 0) / exchangeRate;
  const otherCostsUsd =
    (settlement?.total_other_costs_local || 0) / exchangeRate;
  const totalOperatingCostsUsd = freightUsd + repackingUsd + otherCostsUsd;
  const totalCostsUsd = totalCommercialCostsUsd + totalOperatingCostsUsd;
  const costPerKg = (costUsd: number) =>
    totalKgs > 0 ? costUsd / totalKgs : 0;
  const equivalentBoxCost = (costUsd: number) => costPerKg(costUsd) * boxSize;
  const freightEqBoxUsd = equivalentBoxCost(freightUsd);
  const repackingEqBoxUsd = equivalentBoxCost(repackingUsd);
  const otherCostsEqBoxUsd = equivalentBoxCost(otherCostsUsd);
  const equivalentBoxLabel = t('USD/CEQ {boxSize}kg').replace(
    '{boxSize}',
    formatNumber(boxSize)
  );
  const showExporterCosts =
    costStandard === 'exporter' && exporterCostView.available;
  const exporterTotalCostsUsd = exporterCostView.categoryTotals.reduce(
    (sum, category) => sum + category.amountUsd,
    0
  );
  const displayedFob = showExporterCosts
    ? (settlement?.total_revenue_local || 0) / exchangeRate -
      exporterTotalCostsUsd
    : totalFob;

  return (
    <PageLayout>
      <BackButton href='/settlements/details' label={t('Volver')} />
      {loading ? (
        <div className='space-y-6'>
          {/* Title Skeleton */}
          <div className='space-y-2'>
            <Skeleton className='h-8 w-1/3 rounded-lg' />
            <Skeleton className='h-6 w-1/2 rounded-lg' />
          </div>

          {/* Analysis Card Skeleton */}
          <Card shadow='sm'>
            <CardHeader>
              <Skeleton className='h-8 w-1/4 rounded-lg' />
            </CardHeader>
            <CardBody>
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className='flex w-full min-h-52 flex-col items-center justify-center gap-4 rounded-medium border border-default-200 p-4'
                  >
                    <Skeleton className='rounded-full w-10 h-10' />
                    <Skeleton className='h-4 w-2/3 rounded-lg' />
                    <Skeleton className='h-6 w-1/2 rounded-lg' />
                  </div>
                ))}
              </div>
              <div className='mt-6'>
                <Skeleton className='h-80 w-full rounded-lg' />
              </div>
            </CardBody>
          </Card>

          {/* Costs Analysis Card Skeleton */}
          <Card shadow='sm' className='mt-4'>
            <CardHeader>
              <Skeleton className='h-8 w-1/4 rounded-lg' />
            </CardHeader>
            <CardBody>
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className='flex w-full min-h-32 flex-col items-center justify-center gap-4 rounded-medium border border-default-200 p-4'
                  >
                    <Skeleton className='rounded-full w-10 h-10' />
                    <Skeleton className='h-4 w-2/3 rounded-lg' />
                    <Skeleton className='h-6 w-1/2 rounded-lg' />
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>

          {/* FOB Analysis Card Skeleton */}
          <Card shadow='sm' className='mt-4'>
            <CardHeader>
              <Skeleton className='h-8 w-1/4 rounded-lg' />
            </CardHeader>
            <CardBody>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                {[...Array(2)].map((_, i) => (
                  <div
                    key={i}
                    className='flex w-full min-h-32 flex-col items-center justify-center gap-4 rounded-medium border border-default-200 p-4'
                  >
                    <Skeleton className='rounded-full w-10 h-10' />
                    <Skeleton className='h-4 w-2/3 rounded-lg' />
                    <Skeleton className='h-6 w-1/2 rounded-lg' />
                  </div>
                ))}
              </div>
              <div className='mt-4'>
                <Skeleton className='h-80 w-full rounded-lg' />
              </div>
            </CardBody>
          </Card>
        </div>
      ) : (
        <>
          {isEditOpen && (
            <EditSettlementDialog
              isOpen={isEditOpen}
              onClose={() => {
                setIsEditOpen(false);
              }}
              settlement={settlement}
              onEdit={(updatedSettlement) => {
                fetchDetails(); // Recargar los datos después de editar
              }}
              onDelete={() => {
                // Redirect to settlements list after deletion
                window.location.href = '/settlements/details';
              }}
            />
          )}
          <PageHeader
            title={t('Reporte de Liquidación, Ref: {ref}').replace(
              '{ref}',
              settlement?.reference_number || ''
            )}
            actions={
              <Button
                variant='light'
                startContent={<Icon icon='tabler:edit' />}
                onClick={() => setIsEditOpen(true)}
              >
                {t('Editar')}
              </Button>
            }
          />
          <Card shadow='sm' className='mb-6'>
            <CardBody className='p-6'>
              <div className='grid grid-cols-2 md:grid-cols-4 gap-4 text-sm'>
                <div>
                  <p className='text-default-400 font-medium'>{t('Cliente')}</p>
                  <p>{settlement?.receiver || '—'}</p>
                </div>

                <div>
                  <p className='text-default-400 font-medium'>
                    {t('Mercado de Venta')}
                  </p>
                  <p>{settlement?.market?.name || '—'}</p>
                </div>

                <div>
                  <p className='text-default-400 font-medium'>{t('Nave')}</p>
                  <p>{settlement?.ship_trip?.ship?.name || '—'}</p>
                </div>

                <div>
                  <p className='text-default-400 font-medium'>
                    {t('Puerto de Arribo')}
                  </p>
                  <p>{settlement?.ship_trip?.market?.name || '—'}</p>
                </div>

                <div>
                  <p className='text-default-400 font-medium'>{t('ETD')}</p>
                  <p>
                    {settlement?.ship_trip?.etd
                      ? formatDate(settlement.ship_trip.etd, 'dd MMM yyyy')
                      : '—'}
                  </p>
                </div>

                <div>
                  <p className='text-default-400 font-medium'>{t('ETA')}</p>
                  <p>
                    {settlement?.ship_trip?.eta
                      ? formatDate(settlement.ship_trip.eta, 'dd MMM yyyy')
                      : '—'}
                  </p>
                </div>
                <div>
                  <p className='text-default-400 font-medium'>
                    {t('Fecha de Venta')}
                  </p>
                  <p>
                    {settlement?.date_sold
                      ? formatDate(settlement.date_sold, 'dd MMM yyyy')
                      : '—'}
                  </p>
                </div>
                <div>
                  <p className='text-default-400 font-medium'>
                    {t('Tipo de Cambio')}
                  </p>
                  <p>
                    {settlement?.exchange_rate
                      ? formatNumber(settlement.exchange_rate, { decimals: 2 })
                      : '—'}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card shadow='sm'>
            <CardHeader className='px-6 pt-6'>
              <p className='text-lg font-semibold'>{t('Análisis Venta')}</p>
            </CardHeader>
            <CardBody className='px-6 pb-6'>
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
                {/* 1) Venta Total */}
                <SaleIndicator label={t('Venta Total')}>
                  <p className='text-2xl font-semibold'>
                    {settlement?.local_currency}{' '}
                    {formatNumber(settlement?.total_revenue_local || 0)}
                  </p>
                </SaleIndicator>

                {/* 2) Venta Promedio */}
                <SaleIndicator label={t('Venta Promedio (Caja Eq.)')}>
                  <p className='text-2xl font-semibold'>
                    {settlement?.local_currency}{' '}
                    {formatNumber(averageSaleEqBox, { decimals: 1 })}
                  </p>
                </SaleIndicator>

                {/* 3) Venta vs Mercado (ETA) */}
                <SaleIndicator label={t('Venta vs Mercado (ETA)')}>
                  <p
                    className={`text-2xl font-semibold ${
                      avgDiffETA > 0
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}
                  >
                    {formatNumber(avgDiffETA || 0, { decimals: 2 })}%
                  </p>
                </SaleIndicator>

                {/* 4) Clasificación Kilos ETA */}
                <SaleIndicator label={t('Clasificación Kilos (ETA)')}>
                  <SettlementsLights
                    nulls={performanceEta?.nulls || 0}
                    q1={performanceEta?.q1 || 0}
                    q2={performanceEta?.q2 || 0}
                    q3={performanceEta?.q3 || 0}
                    q4={performanceEta?.q4 || 0}
                    size='md'
                    comparablePercentage={100 - (performanceEta?.nulls || 0)}
                  />
                </SaleIndicator>
              </div>

              <div className='mt-6'>
                <LiquidationDetailsTable
                  settlementDetails={settlement?.settlement_details || []}
                  mode='eta'
                />
              </div>
            </CardBody>
          </Card>

          <Card shadow='sm' className='mt-4'>
            <CardHeader className='px-6 pt-6'>
              <p className='text-lg font-semibold pr-2'>{t('Análisis Costos')}</p>
              {exporterCostView.available && (
                <div className='ml-auto'>
                  <CostStandardToggle
                    value={costStandard}
                    onChange={setCostStandard}
                  />
                </div>
              )}
              {incoterm == 'cif' && (
                <Chip
                  variant='flat'
                  color='warning'
                  onClick={() => setIsEditOpen(true)}
                  className='cursor-pointer hover:scale-110 transition-all'
                  startContent={
                    <Icon icon='tabler:alert-triangle cursor-pointer' />
                  }
                >
                  {t('Falta ingresar flete')}
                </Chip>
              )}
            </CardHeader>
            <CardBody className='px-6 pb-6'>
              {incoterm != 'pre_liquidacion' && showExporterCosts ? (
                <div className='space-y-4'>
                  <AnalysisSection title={t('Costos por categoría exportadora')}>
                    <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
                      {exporterCostView.categoryTotals.map((category) => (
                        <AnalysisMetric
                          key={category.categoryId ?? 'unclassified'}
                          label={
                            category.categoryId === null
                              ? t(category.name)
                              : category.name
                          }
                          value={formatMoneyUSD(category.amountUsd)}
                          icon={
                            category.categoryId === null
                              ? 'tabler:help-circle'
                              : 'tabler:category'
                          }
                          iconClassName={
                            category.categoryId === null
                              ? 'bg-warning-50 text-warning-600 dark:bg-warning-950 dark:text-warning-300'
                              : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                          }
                          valueCaption={t('Total USD')}
                          metrics={[
                            {
                              label: t('% sobre ventas'),
                              value: `${formatNumber(
                                (category.amountLocal / totalRevenueLocal) *
                                  100,
                                { decimals: 2 }
                              )}%`,
                            },
                            {
                              label: t('USD/kg'),
                              value: formatMoneyUSD(
                                costPerKg(category.amountUsd),
                                { decimals: 2 }
                              ),
                            },
                            {
                              label: equivalentBoxLabel,
                              value: formatMoneyUSD(
                                equivalentBoxCost(category.amountUsd),
                                { decimals: 2 }
                              ),
                            },
                          ]}
                        />
                      ))}
                    </div>
                  </AnalysisSection>

                  <AnalysisSection
                    title={t('Costo total')}
                    summary={
                      <CostSummary
                        totalUsd={exporterTotalCostsUsd}
                        totalKgs={totalKgs}
                        boxSize={boxSize}
                        totalLabel={t('Total USD')}
                        perKgLabel={t('USD/kg')}
                        equivalentBoxLabel={equivalentBoxLabel}
                        emphasized
                      />
                    }
                  />
                </div>
              ) : incoterm != 'pre_liquidacion' ? (
                <div className='space-y-4'>
                  <AnalysisSection
                    title={t('Costos comerciales')}
                    summary={
                      <CostSummary
                        totalUsd={totalCommercialCostsUsd}
                        totalKgs={totalKgs}
                        boxSize={boxSize}
                        totalLabel={t('Total USD')}
                        perKgLabel={t('USD/kg')}
                        equivalentBoxLabel={equivalentBoxLabel}
                      />
                    }
                  >
                    <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
                      <AnalysisMetric
                        label={t('Comisión (% sobre ventas)')}
                        value={`${formatNumber(commissionPercent, {
                          decimals: 2,
                        })}%`}
                        icon='tabler:receipt-2'
                        iconClassName='bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-300'
                      />
                      <AnalysisMetric
                        label={t('Impuestos (% sobre ventas)')}
                        value={`${formatNumber(taxPercent, {
                          decimals: 2,
                        })}%`}
                        icon='hugeicons:taxes'
                        iconClassName='bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-300'
                      />
                      <AnalysisMetric
                        label={t('Tipo de cambio USD/{currency}').replace(
                          '{currency}',
                          settlement?.local_currency || ''
                        )}
                        value={formatNumber(settlement?.exchange_rate || 0, {
                          decimals: 2,
                        })}
                        icon='tabler:receipt-yuan'
                        iconClassName='bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-300'
                      />
                    </div>
                  </AnalysisSection>

                  <AnalysisSection
                    title={t('Costos operacionales')}
                    summary={
                      <CostSummary
                        totalUsd={totalOperatingCostsUsd}
                        totalKgs={totalKgs}
                        boxSize={boxSize}
                        totalLabel={t('Total USD')}
                        perKgLabel={t('USD/kg')}
                        equivalentBoxLabel={equivalentBoxLabel}
                      />
                    }
                  >
                    <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
                      <AnalysisMetric
                        label={t('Costo Flete (USD)')}
                        value={
                          settlement?.total_freight_usd
                            ? formatMoneyUSD(settlement.total_freight_usd)
                            : t('Falta el dato')
                        }
                        icon='tabler:ship'
                        iconClassName='bg-cyan-50 text-cyan-600 dark:bg-cyan-950 dark:text-cyan-300'
                        valueClassName={
                          settlement?.total_freight_usd
                            ? 'text-foreground'
                            : 'text-warning-600'
                        }
                        valueCaption={t('Total USD')}
                        metrics={[
                          {
                            label: t('USD/kg'),
                            value: settlement?.total_freight_usd
                              ? formatMoneyUSD(costPerKg(freightUsd), {
                                  decimals: 2,
                                })
                              : '—',
                          },
                          {
                            label: equivalentBoxLabel,
                            value: settlement?.total_freight_usd
                              ? formatMoneyUSD(freightEqBoxUsd, {
                                  decimals: 2,
                                })
                              : '—',
                          },
                        ]}
                      />
                      <AnalysisMetric
                        label={t('Repacking (USD)')}
                        value={formatMoneyUSD(repackingUsd)}
                        icon='tabler:package'
                        iconClassName='bg-violet-50 text-violet-600 dark:bg-violet-950 dark:text-violet-300'
                        valueCaption={t('Total USD')}
                        metrics={[
                          {
                            label: t('USD/kg'),
                            value: formatMoneyUSD(costPerKg(repackingUsd), {
                              decimals: 2,
                            }),
                          },
                          {
                            label: equivalentBoxLabel,
                            value: formatMoneyUSD(repackingEqBoxUsd, {
                              decimals: 2,
                            }),
                          },
                        ]}
                      />
                      <AnalysisMetric
                        label={t('Costos Logísticos y Otros (USD)')}
                        value={formatMoneyUSD(otherCostsUsd)}
                        icon='tabler:dots'
                        iconClassName='bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                        valueCaption={t('Total USD')}
                        metrics={[
                          {
                            label: t('USD/kg'),
                            value: formatMoneyUSD(costPerKg(otherCostsUsd), {
                              decimals: 2,
                            }),
                          },
                          {
                            label: equivalentBoxLabel,
                            value: formatMoneyUSD(otherCostsEqBoxUsd, {
                              decimals: 2,
                            }),
                          },
                        ]}
                      />
                    </div>
                  </AnalysisSection>

                  <AnalysisSection
                    title={t('Costo total')}
                    summary={
                      <CostSummary
                        totalUsd={totalCostsUsd}
                        totalKgs={totalKgs}
                        boxSize={boxSize}
                        totalLabel={t('Total USD')}
                        perKgLabel={t('USD/kg')}
                        equivalentBoxLabel={equivalentBoxLabel}
                        emphasized
                      />
                    }
                  />
                </div>
              ) : (
                <MissingInfoCard onEdit={() => setIsEditOpen(true)} />
              )}
            </CardBody>
          </Card>
          <Card shadow='sm' className='mt-4'>
            <CardHeader className='px-6 pt-6'>
              <p className='text-lg font-semibold'>{t('Análisis FOB')}</p>
              {exporterCostView.available && (
                <div className='ml-auto'>
                  <CostStandardToggle
                    value={costStandard}
                    onChange={setCostStandard}
                  />
                </div>
              )}
            </CardHeader>
            <CardBody className='px-6 pb-6'>
              {incoterm == 'pre_liquidacion' || incoterm == 'cif' ? (
                <MissingInfoCard onEdit={() => setIsEditOpen(true)} />
              ) : (
                <>
                  <AnalysisSection title={t('Resumen FOB')}>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                      <AnalysisMetric
                        label={t('FOB Total (USD)')}
                        value={formatMoneyUSD(displayedFob)}
                        icon='tabler:currency-dollar'
                        iconClassName='bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-300'
                        valueClassName='text-green-700 dark:text-green-300'
                      />
                      <AnalysisMetric
                        label={t('FOB Caja Equivalente (USD)')}
                        value={formatMoneyUSD(
                          (displayedFob / totalKgs) * boxSize
                        )}
                        icon='tabler:box'
                        iconClassName='bg-orange-50 text-orange-600 dark:bg-orange-950 dark:text-orange-300'
                        valueClassName='text-orange-700 dark:text-orange-300'
                      />
                    </div>
                  </AnalysisSection>

                  <div className='mt-4'>
                    <LiquidationFOBTable
                      settlement={settlement}
                      totalFob={displayedFob}
                      totalKgs={totalKgs}
                      boxSize={boxSize}
                      detailCostsUsd={
                        showExporterCosts
                          ? exporterCostView.detailCostsUsd
                          : undefined
                      }
                    />
                  </div>
                </>
              )}
            </CardBody>
          </Card>

          {/*  <div className='mt-4 space-y-4'>
        <LiquidationComparisonChart variety='SANTINA' color='green' />
        <LiquidationComparisonChart variety='Lapins' color='yellow' />
        <LiquidationComparisonChart variety='Regina' color='red' />
      </div> */}
        </>
      )}
    </PageLayout>
  );
};

export default SettlementId;
